export type CellValue = string | number | boolean | null;

export type GridCellChange = {
  rowIndex: number;
  columnKey: string;
  prev: CellValue;
  next: CellValue;
};

export type FormatStyle = {
  background?: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
};

export type OrderFormatting = {
  cells?: Record<string, FormatStyle>;
  rows?: Record<string, FormatStyle>;
  columns?: Record<string, FormatStyle>;
};

const NON_EDITABLE = new Set(["rowIndex", "matchStatus", "select-row", "rdg-select-column"]);

export function isEditableColumnKey(key: string): boolean {
  return !NON_EDITABLE.has(key) && key !== "id";
}

/** Parse clipboard text into a matrix of string cells (TSV / CSV-ish). */
export function parseClipboardMatrix(text: string): string[][] {
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const trimmed = normalized.endsWith("\n") ? normalized.slice(0, -1) : normalized;
  if (!trimmed) return [[""]];
  return trimmed.split("\n").map((line) => line.split("\t"));
}

/**
 * Map a paste matrix onto the grid starting at (startRowIdx in displayRows, startColumnKey).
 * Returns cell changes with previous values for undo.
 */
export function mapPasteToChanges(args: {
  matrix: string[][];
  displayRows: Array<{ rowIndex: number; [key: string]: CellValue }>;
  startDisplayRowIdx: number;
  editableColumnKeys: string[];
  startColumnKey: string;
}): GridCellChange[] {
  const { matrix, displayRows, startDisplayRowIdx, editableColumnKeys, startColumnKey } = args;
  const startCol = editableColumnKeys.indexOf(startColumnKey);
  if (startCol < 0 || matrix.length === 0) return [];

  const changes: GridCellChange[] = [];
  for (let r = 0; r < matrix.length; r++) {
    const displayRow = displayRows[startDisplayRowIdx + r];
    if (!displayRow) break;
    const rowVals = matrix[r] ?? [];
    for (let c = 0; c < rowVals.length; c++) {
      const columnKey = editableColumnKeys[startCol + c];
      if (!columnKey) break;
      const raw = rowVals[c] ?? "";
      const prev = (displayRow[columnKey] ?? null) as CellValue;
      const next: CellValue = columnKey === "quantity" ? Number(raw) : raw;
      if (Object.is(prev, next) || String(prev ?? "") === String(next ?? "")) continue;
      changes.push({ rowIndex: displayRow.rowIndex, columnKey, prev, next });
    }
  }
  return changes;
}

export function mergeFormatStyle(
  formatting: OrderFormatting | undefined,
  rowIndex: number,
  columnKey: string,
): FormatStyle {
  const col = formatting?.columns?.[columnKey] ?? {};
  const row = formatting?.rows?.[String(rowIndex)] ?? {};
  const cell = formatting?.cells?.[`${rowIndex}:${columnKey}`] ?? {};
  return { ...col, ...row, ...cell };
}

function escHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function styleAttr(style: FormatStyle): string {
  const parts: string[] = [];
  if (style.background) parts.push(`background:${style.background}`);
  if (style.color) parts.push(`color:${style.color}`);
  if (style.bold) parts.push("font-weight:bold");
  if (style.italic) parts.push("font-style:italic");
  return parts.length ? ` style="${parts.join(";")}"` : "";
}

/** Excel-friendly HTML workbook preserving highlight / bold / italic. */
export function buildFormattedExcelHtml(args: {
  title: string;
  headers: string[];
  columnKeys: string[];
  rows: Array<{ rowIndex: number; values: Record<string, CellValue> }>;
  formatting?: OrderFormatting;
}): string {
  const headerRow = args.headers.map((h) => `<th>${escHtml(h)}</th>`).join("");
  const body = args.rows
    .map((row) => {
      const cells = args.columnKeys
        .map((key) => {
          const style = mergeFormatStyle(args.formatting, row.rowIndex, key);
          return `<td${styleAttr(style)}>${escHtml(row.values[key] ?? "")}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="utf-8"><title>${escHtml(args.title)}</title>
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Order</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body><table border="1"><thead><tr>${headerRow}</tr></thead><tbody>${body}</tbody></table></body>
</html>`;
}

export function downloadBlob(fileName: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export function compareCellValues(a: CellValue, b: CellValue, dir: "asc" | "desc"): number {
  if (typeof a === "number" && typeof b === "number") {
    return dir === "asc" ? a - b : b - a;
  }
  const cmp = String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true });
  return dir === "asc" ? cmp : -cmp;
}
